// Beta changelog — drives the "What's New" popup on first daily launch.
//
// Why 72 hours: beta testers don't reliably sign in every day. A 24h window
// would silently hide everything from a tester who skipped a day, and 48h
// has the same gap if they take a weekend off. 72h covers the typical "I
// haven't checked in for a couple days" case while still feeling fresh —
// anything older has lost its "hey, look at this" charge. Tunable here.
export const WHATS_NEW_WINDOW_HOURS = 72;

export type ChangelogKind = 'feature' | 'fix' | 'improvement';

export type ChangelogEntry = {
  /** Stable id — not currently used for dedupe but useful for analytics later. */
  id: string;
  kind: ChangelogKind;
  /** ISO timestamp the entry was shipped. The popup filters by this against
   *  the WHATS_NEW_WINDOW_HOURS cutoff. */
  shippedAt: string;
  /** i18n keys that resolve to the title + 1-2 sentence body. Keep bodies
   *  short — the popup is a glance, not a doc. */
  titleKey: string;
  bodyKey: string;
};

// Newest first. When you ship a feature, prepend an entry. Old entries can
// stay — they roll off automatically as the timestamp ages past the window.
export const CHANGELOG: ChangelogEntry[] = [
  {
    id: '2026-05-16-sports-coach-voice',
    kind: 'feature',
    shippedAt: '2026-05-16T20:00:00Z',
    titleKey: 'whatsnew.entry.sportsCoachVoice.title',
    bodyKey: 'whatsnew.entry.sportsCoachVoice.body',
  },
  {
    id: '2026-05-16-streak-rescue-fix',
    kind: 'fix',
    shippedAt: '2026-05-16T14:00:00Z',
    titleKey: 'whatsnew.entry.streakRescueFix.title',
    bodyKey: 'whatsnew.entry.streakRescueFix.body',
  },
  {
    id: '2026-05-16-streak-lazy-break-detection',
    kind: 'fix',
    shippedAt: '2026-05-16T06:00:00Z',
    titleKey: 'whatsnew.entry.streakLazyBreakDetection.title',
    bodyKey: 'whatsnew.entry.streakLazyBreakDetection.body',
  },
  {
    id: '2026-05-16-source-and-payment-capture',
    kind: 'improvement',
    shippedAt: '2026-05-16T04:00:00Z',
    titleKey: 'whatsnew.entry.sourceAndPaymentCapture.title',
    bodyKey: 'whatsnew.entry.sourceAndPaymentCapture.body',
  },
  {
    id: '2026-05-16-rating-at-cut-end',
    kind: 'improvement',
    shippedAt: '2026-05-16T02:00:00Z',
    titleKey: 'whatsnew.entry.ratingAtCutEnd.title',
    bodyKey: 'whatsnew.entry.ratingAtCutEnd.body',
  },
  {
    id: '2026-05-15-source-at-checkin',
    kind: 'improvement',
    shippedAt: '2026-05-16T00:00:00Z',
    titleKey: 'whatsnew.entry.sourceAtCheckin.title',
    bodyKey: 'whatsnew.entry.sourceAtCheckin.body',
  },
  {
    id: '2026-05-15-capture-framing-v1-1',
    kind: 'improvement',
    shippedAt: '2026-05-15T22:00:00Z',
    titleKey: 'whatsnew.entry.captureFramingV1_1.title',
    bodyKey: 'whatsnew.entry.captureFramingV1_1.body',
  },
  {
    id: '2026-05-15-capture-framing-guides',
    kind: 'feature',
    shippedAt: '2026-05-15T18:00:00Z',
    titleKey: 'whatsnew.entry.captureFramingGuides.title',
    bodyKey: 'whatsnew.entry.captureFramingGuides.body',
  },
  {
    id: '2026-05-15-transformation-picture-frame',
    kind: 'feature',
    shippedAt: '2026-05-15T16:00:00Z',
    titleKey: 'whatsnew.entry.transformationPictureFrame.title',
    bodyKey: 'whatsnew.entry.transformationPictureFrame.body',
  },
  {
    id: '2026-05-15-menu-source-of-truth',
    kind: 'fix',
    shippedAt: '2026-05-15T12:00:00Z',
    titleKey: 'whatsnew.entry.menuSourceOfTruth.title',
    bodyKey: 'whatsnew.entry.menuSourceOfTruth.body',
  },
  {
    id: '2026-05-15-noshow-confirm-and-plurals',
    kind: 'improvement',
    shippedAt: '2026-05-15T10:00:00Z',
    titleKey: 'whatsnew.entry.noShowConfirmAndPlurals.title',
    bodyKey: 'whatsnew.entry.noShowConfirmAndPlurals.body',
  },
  {
    id: '2026-05-15-capacity-patterns-and-channels',
    kind: 'feature',
    shippedAt: '2026-05-15T08:00:00Z',
    titleKey: 'whatsnew.entry.capacityPatternsAndChannels.title',
    bodyKey: 'whatsnew.entry.capacityPatternsAndChannels.body',
  },
  {
    id: '2026-05-15-insight-patterns-round-2',
    kind: 'feature',
    shippedAt: '2026-05-15T06:00:00Z',
    titleKey: 'whatsnew.entry.insightPatternsRound2.title',
    bodyKey: 'whatsnew.entry.insightPatternsRound2.body',
  },
  {
    id: '2026-05-15-insight-patterns-batch',
    kind: 'feature',
    shippedAt: '2026-05-15T04:00:00Z',
    titleKey: 'whatsnew.entry.insightPatternsBatch.title',
    bodyKey: 'whatsnew.entry.insightPatternsBatch.body',
  },
  {
    id: '2026-05-14-no-shows-and-insights',
    kind: 'feature',
    shippedAt: '2026-05-15T02:00:00Z',
    titleKey: 'whatsnew.entry.noShowsAndInsights.title',
    bodyKey: 'whatsnew.entry.noShowsAndInsights.body',
  },
  {
    id: '2026-05-14-cut-quality-and-source',
    kind: 'feature',
    shippedAt: '2026-05-14T22:00:00Z',
    titleKey: 'whatsnew.entry.cutQualityAndSource.title',
    bodyKey: 'whatsnew.entry.cutQualityAndSource.body',
  },
  {
    id: '2026-05-14-averages-and-churn',
    kind: 'feature',
    shippedAt: '2026-05-14T14:00:00Z',
    titleKey: 'whatsnew.entry.averagesAndChurn.title',
    bodyKey: 'whatsnew.entry.averagesAndChurn.body',
  },
  {
    id: '2026-05-14-retention-metrics',
    kind: 'feature',
    shippedAt: '2026-05-14T06:00:00Z',
    titleKey: 'whatsnew.entry.retentionMetrics.title',
    bodyKey: 'whatsnew.entry.retentionMetrics.body',
  },
  {
    id: '2026-05-14-unified-heatmap',
    kind: 'feature',
    shippedAt: '2026-05-14T00:00:00Z',
    titleKey: 'whatsnew.entry.unifiedHeatmap.title',
    bodyKey: 'whatsnew.entry.unifiedHeatmap.body',
  },
  {
    id: '2026-05-13-capacity-weather-front-and-center',
    kind: 'improvement',
    shippedAt: '2026-05-13T22:00:00Z',
    titleKey: 'whatsnew.entry.capacityWeatherFrontCenter.title',
    bodyKey: 'whatsnew.entry.capacityWeatherFrontCenter.body',
  },
  {
    id: '2026-05-13-walkin-aware-capacity',
    kind: 'feature',
    shippedAt: '2026-05-13T20:00:00Z',
    titleKey: 'whatsnew.entry.walkinAwareCapacity.title',
    bodyKey: 'whatsnew.entry.walkinAwareCapacity.body',
  },
  {
    id: '2026-05-13-forecast-and-walkin',
    kind: 'feature',
    shippedAt: '2026-05-13T18:00:00Z',
    titleKey: 'whatsnew.entry.forecastAndWalkin.title',
    bodyKey: 'whatsnew.entry.forecastAndWalkin.body',
  },
  {
    id: '2026-05-13-custom-date-range',
    kind: 'feature',
    shippedAt: '2026-05-13T02:00:00Z',
    titleKey: 'whatsnew.entry.customDateRange.title',
    bodyKey: 'whatsnew.entry.customDateRange.body',
  },
  {
    id: '2026-05-13-payment-method-and-1099k',
    kind: 'feature',
    shippedAt: '2026-05-13T01:00:00Z',
    titleKey: 'whatsnew.entry.paymentMethod1099k.title',
    bodyKey: 'whatsnew.entry.paymentMethod1099k.body',
  },
  {
    id: '2026-05-13-recap-pdf-share-and-capacity',
    kind: 'feature',
    shippedAt: '2026-05-13T00:00:00Z',
    titleKey: 'whatsnew.entry.recapPdfShareCapacity.title',
    bodyKey: 'whatsnew.entry.recapPdfShareCapacity.body',
  },
  {
    id: '2026-05-12-recaps-tax-prep-detail',
    kind: 'feature',
    shippedAt: '2026-05-12T12:00:00Z',
    titleKey: 'whatsnew.entry.recapsTaxPrep.title',
    bodyKey: 'whatsnew.entry.recapsTaxPrep.body',
  },
  {
    id: '2026-05-12-recaps-quarterly-annual-and-tip-insights',
    kind: 'feature',
    shippedAt: '2026-05-12T11:00:00Z',
    titleKey: 'whatsnew.entry.recapsAndTipInsights.title',
    bodyKey: 'whatsnew.entry.recapsAndTipInsights.body',
  },
  {
    id: '2026-05-12-tips-surfaces-and-big-tipper',
    kind: 'feature',
    shippedAt: '2026-05-12T10:00:00Z',
    titleKey: 'whatsnew.entry.tipsSurfacesBigTipper.title',
    bodyKey: 'whatsnew.entry.tipsSurfacesBigTipper.body',
  },
  {
    id: '2026-05-12-revenue-breakdown-popup',
    kind: 'improvement',
    shippedAt: '2026-05-12T09:00:00Z',
    titleKey: 'whatsnew.entry.revenueBreakdownPopup.title',
    bodyKey: 'whatsnew.entry.revenueBreakdownPopup.body',
  },
  {
    id: '2026-05-12-tip-capture-on-cut-complete',
    kind: 'feature',
    shippedAt: '2026-05-12T08:00:00Z',
    titleKey: 'whatsnew.entry.tipCapture.title',
    bodyKey: 'whatsnew.entry.tipCapture.body',
  },
  {
    id: '2026-05-12-dashboard-top-menu-and-capacity-title',
    kind: 'feature',
    shippedAt: '2026-05-12T07:00:00Z',
    titleKey: 'whatsnew.entry.dashboardTopMenu.title',
    bodyKey: 'whatsnew.entry.dashboardTopMenu.body',
  },
  {
    id: '2026-05-12-most-requested-and-progress-fix',
    kind: 'feature',
    shippedAt: '2026-05-12T06:00:00Z',
    titleKey: 'whatsnew.entry.mostRequestedAndProgressFix.title',
    bodyKey: 'whatsnew.entry.mostRequestedAndProgressFix.body',
  },
  {
    id: '2026-05-12-services-dedupe-and-numbered-duplicate',
    kind: 'improvement',
    shippedAt: '2026-05-12T05:00:00Z',
    titleKey: 'whatsnew.entry.servicesDedupeRepass.title',
    bodyKey: 'whatsnew.entry.servicesDedupeRepass.body',
  },
  {
    id: '2026-05-12-birthday-picker-wheel',
    kind: 'improvement',
    shippedAt: '2026-05-12T04:00:00Z',
    titleKey: 'whatsnew.entry.birthdayPickerWheel.title',
    bodyKey: 'whatsnew.entry.birthdayPickerWheel.body',
  },
  {
    id: '2026-05-12-client-birthdays-csv',
    kind: 'improvement',
    shippedAt: '2026-05-12T03:00:00Z',
    titleKey: 'whatsnew.entry.clientBirthdaysCsv.title',
    bodyKey: 'whatsnew.entry.clientBirthdaysCsv.body',
  },
  {
    id: '2026-05-12-client-birthdays',
    kind: 'feature',
    shippedAt: '2026-05-12T02:00:00Z',
    titleKey: 'whatsnew.entry.clientBirthdays.title',
    bodyKey: 'whatsnew.entry.clientBirthdays.body',
  },
  {
    id: '2026-05-12-cut-timer-menu-csv',
    kind: 'feature',
    shippedAt: '2026-05-12T01:00:00Z',
    titleKey: 'whatsnew.entry.cutTimerMenuCsv.title',
    bodyKey: 'whatsnew.entry.cutTimerMenuCsv.body',
  },
  {
    id: '2026-05-11-revenue-chart-past-weather',
    kind: 'feature',
    shippedAt: '2026-05-12T00:00:00Z',
    titleKey: 'whatsnew.entry.revenueChartPastWeather.title',
    bodyKey: 'whatsnew.entry.revenueChartPastWeather.body',
  },
  {
    id: '2026-05-11-utilization-explainer-weather-fix',
    kind: 'improvement',
    shippedAt: '2026-05-11T23:00:00Z',
    titleKey: 'whatsnew.entry.utilizationExplainerWeatherFix.title',
    bodyKey: 'whatsnew.entry.utilizationExplainerWeatherFix.body',
  },
  {
    id: '2026-05-11-lessons-filter-chips',
    kind: 'improvement',
    shippedAt: '2026-05-11T22:00:00Z',
    titleKey: 'whatsnew.entry.lessonsFilterChips.title',
    bodyKey: 'whatsnew.entry.lessonsFilterChips.body',
  },
  {
    id: '2026-05-11-recap-share-image',
    kind: 'improvement',
    shippedAt: '2026-05-11T21:00:00Z',
    titleKey: 'whatsnew.entry.recapShareImage.title',
    bodyKey: 'whatsnew.entry.recapShareImage.body',
  },
  {
    id: '2026-05-11-outlook-explainers',
    kind: 'feature',
    shippedAt: '2026-05-11T20:00:00Z',
    titleKey: 'whatsnew.entry.outlookExplainers.title',
    bodyKey: 'whatsnew.entry.outlookExplainers.body',
  },
  {
    id: '2026-05-11-six-min-floor',
    kind: 'improvement',
    shippedAt: '2026-05-11T19:00:00Z',
    titleKey: 'whatsnew.entry.sixMinFloor.title',
    bodyKey: 'whatsnew.entry.sixMinFloor.body',
  },
  {
    id: '2026-05-11-heatmap-sliding-window',
    kind: 'improvement',
    shippedAt: '2026-05-11T18:00:00Z',
    titleKey: 'whatsnew.entry.heatmapSlidingWindow.title',
    bodyKey: 'whatsnew.entry.heatmapSlidingWindow.body',
  },
  {
    id: '2026-05-11-recap-pdf',
    kind: 'feature',
    shippedAt: '2026-05-11T17:00:00Z',
    titleKey: 'whatsnew.entry.recapPdf.title',
    bodyKey: 'whatsnew.entry.recapPdf.body',
  },
  {
    id: '2026-05-11-lessons-bookshelf',
    kind: 'feature',
    shippedAt: '2026-05-11T16:00:00Z',
    titleKey: 'whatsnew.entry.lessonsBookshelf.title',
    bodyKey: 'whatsnew.entry.lessonsBookshelf.body',
  },
  {
    id: '2026-05-11-hours-days-chips',
    kind: 'improvement',
    shippedAt: '2026-05-11T15:00:00Z',
    titleKey: 'whatsnew.entry.hoursDaysChips.title',
    bodyKey: 'whatsnew.entry.hoursDaysChips.body',
  },
  {
    id: '2026-05-11-velvet-concierge-timer',
    kind: 'feature',
    shippedAt: '2026-05-11T14:00:00Z',
    titleKey: 'whatsnew.entry.velvetConciergeTimer.title',
    bodyKey: 'whatsnew.entry.velvetConciergeTimer.body',
  },
  {
    id: '2026-05-11-earning-rate-range',
    kind: 'improvement',
    shippedAt: '2026-05-11T13:00:00Z',
    titleKey: 'whatsnew.entry.earningRateRange.title',
    bodyKey: 'whatsnew.entry.earningRateRange.body',
  },
  {
    id: '2026-05-10-cut-log-edit',
    kind: 'feature',
    shippedAt: '2026-05-10T18:00:00Z',
    titleKey: 'whatsnew.entry.cutLogEdit.title',
    bodyKey: 'whatsnew.entry.cutLogEdit.body',
  },
  {
    id: '2026-05-10-target-chip-on-tile',
    kind: 'improvement',
    shippedAt: '2026-05-10T17:30:00Z',
    titleKey: 'whatsnew.entry.targetChipOnTile.title',
    bodyKey: 'whatsnew.entry.targetChipOnTile.body',
  },
  {
    id: '2026-05-10-earnings-outlook',
    kind: 'feature',
    shippedAt: '2026-05-10T17:00:00Z',
    titleKey: 'whatsnew.entry.earningsOutlook.title',
    bodyKey: 'whatsnew.entry.earningsOutlook.body',
  },
  {
    id: '2026-05-10-hourly-target-gradient',
    kind: 'feature',
    shippedAt: '2026-05-10T16:30:00Z',
    titleKey: 'whatsnew.entry.hourlyTargetGradient.title',
    bodyKey: 'whatsnew.entry.hourlyTargetGradient.body',
  },
  {
    id: '2026-05-10-target-days-per-week',
    kind: 'feature',
    shippedAt: '2026-05-10T16:00:00Z',
    titleKey: 'whatsnew.entry.targetDaysPerWeek.title',
    bodyKey: 'whatsnew.entry.targetDaysPerWeek.body',
  },
  {
    id: '2026-05-10-greeting-cutoffs',
    kind: 'improvement',
    shippedAt: '2026-05-10T15:30:00Z',
    titleKey: 'whatsnew.entry.greetingCutoffs.title',
    bodyKey: 'whatsnew.entry.greetingCutoffs.body',
  },
  {
    id: '2026-05-10-heatmap-today-row',
    kind: 'improvement',
    shippedAt: '2026-05-10T15:00:00Z',
    titleKey: 'whatsnew.entry.heatmapTodayRow.title',
    bodyKey: 'whatsnew.entry.heatmapTodayRow.body',
  },
  {
    id: '2026-05-10-heatmap-simplified',
    kind: 'improvement',
    shippedAt: '2026-05-10T14:30:00Z',
    titleKey: 'whatsnew.entry.heatmapSimplified.title',
    bodyKey: 'whatsnew.entry.heatmapSimplified.body',
  },
  {
    id: '2026-05-10-heatmap-gap-floor',
    kind: 'fix',
    shippedAt: '2026-05-10T14:00:00Z',
    titleKey: 'whatsnew.entry.heatmapGapFloor.title',
    bodyKey: 'whatsnew.entry.heatmapGapFloor.body',
  },
  {
    id: '2026-05-10-metrics-4-tiles-deltas',
    kind: 'improvement',
    shippedAt: '2026-05-10T13:30:00Z',
    titleKey: 'whatsnew.entry.metrics4TilesDeltas.title',
    bodyKey: 'whatsnew.entry.metrics4TilesDeltas.body',
  },
  {
    id: '2026-05-10-notifications-tab-retired',
    kind: 'improvement',
    shippedAt: '2026-05-10T13:00:00Z',
    titleKey: 'whatsnew.entry.notificationsTabRetired.title',
    bodyKey: 'whatsnew.entry.notificationsTabRetired.body',
  },
  {
    id: '2026-05-10-settings-consolidation',
    kind: 'improvement',
    shippedAt: '2026-05-10T12:30:00Z',
    titleKey: 'whatsnew.entry.settingsConsolidation.title',
    bodyKey: 'whatsnew.entry.settingsConsolidation.body',
  },
  {
    id: '2026-05-10-tiers-top-journey-embed',
    kind: 'improvement',
    shippedAt: '2026-05-10T12:00:00Z',
    titleKey: 'whatsnew.entry.tiersTopJourneyEmbed.title',
    bodyKey: 'whatsnew.entry.tiersTopJourneyEmbed.body',
  },
  {
    id: '2026-05-10-capacity-week-nav-polish',
    kind: 'improvement',
    shippedAt: '2026-05-10T11:30:00Z',
    titleKey: 'whatsnew.entry.capacityWeekNavPolish.title',
    bodyKey: 'whatsnew.entry.capacityWeekNavPolish.body',
  },
  {
    id: '2026-05-10-recent-achievements-ranged',
    kind: 'improvement',
    shippedAt: '2026-05-10T11:00:00Z',
    titleKey: 'whatsnew.entry.recentAchievementsRanged.title',
    bodyKey: 'whatsnew.entry.recentAchievementsRanged.body',
  },
  {
    id: '2026-05-10-atrisk-ack-checkbox',
    kind: 'improvement',
    shippedAt: '2026-05-10T10:30:00Z',
    titleKey: 'whatsnew.entry.atRiskAckCheckbox.title',
    bodyKey: 'whatsnew.entry.atRiskAckCheckbox.body',
  },
  {
    id: '2026-05-10-tiers-as-filter',
    kind: 'improvement',
    shippedAt: '2026-05-10T10:00:00Z',
    titleKey: 'whatsnew.entry.tiersAsFilter.title',
    bodyKey: 'whatsnew.entry.tiersAsFilter.body',
  },
  {
    id: '2026-05-10-clients-filter-chips',
    kind: 'feature',
    shippedAt: '2026-05-10T09:30:00Z',
    titleKey: 'whatsnew.entry.clientsFilterChips.title',
    bodyKey: 'whatsnew.entry.clientsFilterChips.body',
  },
  {
    id: '2026-05-10-tappable-metrics-cutlist',
    kind: 'feature',
    shippedAt: '2026-05-10T09:00:00Z',
    titleKey: 'whatsnew.entry.tappableMetricsCutlist.title',
    bodyKey: 'whatsnew.entry.tappableMetricsCutlist.body',
  },
  {
    id: '2026-05-10-today-dash-shuffle',
    kind: 'improvement',
    shippedAt: '2026-05-10T08:30:00Z',
    titleKey: 'whatsnew.entry.todayDashShuffle.title',
    bodyKey: 'whatsnew.entry.todayDashShuffle.body',
  },
  {
    id: '2026-05-10-leaderboard-podium-icons',
    kind: 'improvement',
    shippedAt: '2026-05-10T08:00:00Z',
    titleKey: 'whatsnew.entry.leaderboardPodiumIcons.title',
    bodyKey: 'whatsnew.entry.leaderboardPodiumIcons.body',
  },
  {
    id: '2026-05-10-top-clients-clients-tab-only',
    kind: 'improvement',
    shippedAt: '2026-05-10T07:30:00Z',
    titleKey: 'whatsnew.entry.topClientsClientsTabOnly.title',
    bodyKey: 'whatsnew.entry.topClientsClientsTabOnly.body',
  },
  {
    id: '2026-05-10-dash-reorder',
    kind: 'improvement',
    shippedAt: '2026-05-10T07:00:00Z',
    titleKey: 'whatsnew.entry.dashReorder.title',
    bodyKey: 'whatsnew.entry.dashReorder.body',
  },
  {
    id: '2026-05-10-atrisk-on-today',
    kind: 'improvement',
    shippedAt: '2026-05-10T06:30:00Z',
    titleKey: 'whatsnew.entry.atRiskOnToday.title',
    bodyKey: 'whatsnew.entry.atRiskOnToday.body',
  },
  {
    id: '2026-05-10-clients-tab-restructured',
    kind: 'improvement',
    shippedAt: '2026-05-10T06:00:00Z',
    titleKey: 'whatsnew.entry.clientsTabRestructured.title',
    bodyKey: 'whatsnew.entry.clientsTabRestructured.body',
  },
  {
    id: '2026-05-10-dash-range-7d-default',
    kind: 'improvement',
    shippedAt: '2026-05-10T05:30:00Z',
    titleKey: 'whatsnew.entry.dashRange7dDefault.title',
    bodyKey: 'whatsnew.entry.dashRange7dDefault.body',
  },
  {
    id: '2026-05-10-week-at-a-glance',
    kind: 'feature',
    shippedAt: '2026-05-10T05:00:00Z',
    titleKey: 'whatsnew.entry.weekAtAGlance.title',
    bodyKey: 'whatsnew.entry.weekAtAGlance.body',
  },
  {
    id: '2026-05-10-heatmap-yellow-partial',
    kind: 'improvement',
    shippedAt: '2026-05-10T04:00:00Z',
    titleKey: 'whatsnew.entry.heatmapYellowPartial.title',
    bodyKey: 'whatsnew.entry.heatmapYellowPartial.body',
  },
  {
    id: '2026-05-10-capacity-tile-minutes',
    kind: 'fix',
    shippedAt: '2026-05-10T03:30:00Z',
    titleKey: 'whatsnew.entry.capacityTileMinutes.title',
    bodyKey: 'whatsnew.entry.capacityTileMinutes.body',
  },
  {
    id: '2026-05-10-target-hours-first-save',
    kind: 'fix',
    shippedAt: '2026-05-10T03:00:00Z',
    titleKey: 'whatsnew.entry.targetHoursFirstSave.title',
    bodyKey: 'whatsnew.entry.targetHoursFirstSave.body',
  },
  {
    id: '2026-05-10-realtime-streak-celebrations',
    kind: 'fix',
    shippedAt: '2026-05-10T02:30:00Z',
    titleKey: 'whatsnew.entry.realtimeStreakCelebrations.title',
    bodyKey: 'whatsnew.entry.realtimeStreakCelebrations.body',
  },
  {
    id: '2026-05-10-shields-panel-removed',
    kind: 'improvement',
    shippedAt: '2026-05-10T02:25:00Z',
    titleKey: 'whatsnew.entry.shieldsPanelRemoved.title',
    bodyKey: 'whatsnew.entry.shieldsPanelRemoved.body',
  },
  {
    id: '2026-05-10-weekly-target-reminder',
    kind: 'improvement',
    shippedAt: '2026-05-10T01:30:00Z',
    titleKey: 'whatsnew.entry.weeklyTargetReminder.title',
    bodyKey: 'whatsnew.entry.weeklyTargetReminder.body',
  },
  {
    id: '2026-05-09-clients-dedupe-phone',
    kind: 'fix',
    shippedAt: '2026-05-10T00:30:00Z',
    titleKey: 'whatsnew.entry.clientsDedupePhone.title',
    bodyKey: 'whatsnew.entry.clientsDedupePhone.body',
  },
  {
    id: '2026-05-09-services-dedupe',
    kind: 'fix',
    shippedAt: '2026-05-09T23:30:00Z',
    titleKey: 'whatsnew.entry.servicesDedupe.title',
    bodyKey: 'whatsnew.entry.servicesDedupe.body',
  },
  {
    id: '2026-05-09-streak-celebration-popups',
    kind: 'feature',
    shippedAt: '2026-05-09T22:30:00Z',
    titleKey: 'whatsnew.entry.streakCelebrationPopups.title',
    bodyKey: 'whatsnew.entry.streakCelebrationPopups.body',
  },
  {
    id: '2026-05-09-clients-scroll-fix',
    kind: 'fix',
    shippedAt: '2026-05-09T22:00:00Z',
    titleKey: 'whatsnew.entry.clientsScrollFix.title',
    bodyKey: 'whatsnew.entry.clientsScrollFix.body',
  },
  {
    id: '2026-05-09-client-list-ios-contacts',
    kind: 'improvement',
    shippedAt: '2026-05-09T21:00:00Z',
    titleKey: 'whatsnew.entry.clientListIosContacts.title',
    bodyKey: 'whatsnew.entry.clientListIosContacts.body',
  },
  {
    id: '2026-05-09-achievements-reordered',
    kind: 'improvement',
    shippedAt: '2026-05-09T20:00:00Z',
    titleKey: 'whatsnew.entry.achievementsReordered.title',
    bodyKey: 'whatsnew.entry.achievementsReordered.body',
  },
  {
    id: '2026-05-09-streak-backfill',
    kind: 'fix',
    shippedAt: '2026-05-09T19:30:00Z',
    titleKey: 'whatsnew.entry.streakBackfill.title',
    bodyKey: 'whatsnew.entry.streakBackfill.body',
  },
  {
    id: '2026-05-09-video-clip-bounds',
    kind: 'improvement',
    shippedAt: '2026-05-09T18:00:00Z',
    titleKey: 'whatsnew.entry.videoClipBounds.title',
    bodyKey: 'whatsnew.entry.videoClipBounds.body',
  },
  {
    id: '2026-05-09-rescue-confirm',
    kind: 'improvement',
    shippedAt: '2026-05-09T16:00:00Z',
    titleKey: 'whatsnew.entry.rescueConfirm.title',
    bodyKey: 'whatsnew.entry.rescueConfirm.body',
  },
  {
    id: '2026-05-09-streak-status-everywhere',
    kind: 'improvement',
    shippedAt: '2026-05-09T14:00:00Z',
    titleKey: 'whatsnew.entry.streakStatusEverywhere.title',
    bodyKey: 'whatsnew.entry.streakStatusEverywhere.body',
  },
  {
    id: '2026-05-09-camera-orientation-fix',
    kind: 'fix',
    shippedAt: '2026-05-09T12:00:00Z',
    titleKey: 'whatsnew.entry.cameraOrientationFix.title',
    bodyKey: 'whatsnew.entry.cameraOrientationFix.body',
  },
  {
    id: '2026-05-09-achievement-integrity',
    kind: 'improvement',
    shippedAt: '2026-05-09T10:00:00Z',
    titleKey: 'whatsnew.entry.achievementIntegrity.title',
    bodyKey: 'whatsnew.entry.achievementIntegrity.body',
  },
  {
    id: '2026-05-09-streak-shield-protection',
    kind: 'feature',
    shippedAt: '2026-05-09T08:00:00Z',
    titleKey: 'whatsnew.entry.streakShieldProtection.title',
    bodyKey: 'whatsnew.entry.streakShieldProtection.body',
  },
  {
    id: '2026-05-08-clients-visit-required',
    kind: 'fix',
    shippedAt: '2026-05-08T13:00:00Z',
    titleKey: 'whatsnew.entry.clientsVisitRequired.title',
    bodyKey: 'whatsnew.entry.clientsVisitRequired.body',
  },
  {
    id: '2026-05-08-top-tier-client-badges',
    kind: 'feature',
    shippedAt: '2026-05-08T11:00:00Z',
    titleKey: 'whatsnew.entry.topTierClientBadges.title',
    bodyKey: 'whatsnew.entry.topTierClientBadges.body',
  },
  {
    id: '2026-05-07-timekeeper-and-retier',
    kind: 'feature',
    shippedAt: '2026-05-07T19:30:00Z',
    titleKey: 'whatsnew.entry.timekeeperAndRetier.title',
    bodyKey: 'whatsnew.entry.timekeeperAndRetier.body',
  },
  {
    id: '2026-05-07-vacation-and-target-history',
    kind: 'feature',
    shippedAt: '2026-05-07T18:00:00Z',
    titleKey: 'whatsnew.entry.vacationAndTargetHistory.title',
    bodyKey: 'whatsnew.entry.vacationAndTargetHistory.body',
  },
  {
    id: '2026-05-07-capacity-utilization',
    kind: 'feature',
    shippedAt: '2026-05-07T17:00:00Z',
    titleKey: 'whatsnew.entry.capacityUtilization.title',
    bodyKey: 'whatsnew.entry.capacityUtilization.body',
  },
  {
    id: '2026-05-07-quiet-day-playbook',
    kind: 'feature',
    shippedAt: '2026-05-07T16:00:00Z',
    titleKey: 'whatsnew.entry.quietDayPlaybook.title',
    bodyKey: 'whatsnew.entry.quietDayPlaybook.body',
  },
  {
    id: '2026-05-07-whatsnew-version-aware',
    kind: 'improvement',
    shippedAt: '2026-05-07T15:30:00Z',
    titleKey: 'whatsnew.entry.whatsnewVersionAware.title',
    bodyKey: 'whatsnew.entry.whatsnewVersionAware.body',
  },
  {
    id: '2026-05-07-weather-peak-slow',
    kind: 'improvement',
    shippedAt: '2026-05-07T15:00:00Z',
    titleKey: 'whatsnew.entry.weatherPeakSlow.title',
    bodyKey: 'whatsnew.entry.weatherPeakSlow.body',
  },
  {
    id: '2026-05-07-cuttimer-alpha-bar',
    kind: 'feature',
    shippedAt: '2026-05-07T14:30:00Z',
    titleKey: 'whatsnew.entry.cutTimerAlphaBar.title',
    bodyKey: 'whatsnew.entry.cutTimerAlphaBar.body',
  },
  {
    id: '2026-05-07-scroll-chain-fix',
    kind: 'fix',
    shippedAt: '2026-05-07T14:00:00Z',
    titleKey: 'whatsnew.entry.scrollChainFix.title',
    bodyKey: 'whatsnew.entry.scrollChainFix.body',
  },
  {
    id: '2026-05-07-photo-share-consent',
    kind: 'feature',
    shippedAt: '2026-05-07T11:00:00Z',
    titleKey: 'whatsnew.entry.photoShareConsent.title',
    bodyKey: 'whatsnew.entry.photoShareConsent.body',
  },
  {
    id: '2026-05-07-post-cut-sticky-footer',
    kind: 'improvement',
    shippedAt: '2026-05-07T10:30:00Z',
    titleKey: 'whatsnew.entry.postCutStickyFooter.title',
    bodyKey: 'whatsnew.entry.postCutStickyFooter.body',
  },
  {
    id: '2026-05-07-discard-fix',
    kind: 'fix',
    shippedAt: '2026-05-07T10:25:00Z',
    titleKey: 'whatsnew.entry.discardFix.title',
    bodyKey: 'whatsnew.entry.discardFix.body',
  },
  {
    id: '2026-05-07-splash-once-a-day',
    kind: 'fix',
    shippedAt: '2026-05-07T10:20:00Z',
    titleKey: 'whatsnew.entry.splashOnceADay.title',
    bodyKey: 'whatsnew.entry.splashOnceADay.body',
  },
  {
    id: '2026-05-07-client-list-alpha-bar',
    kind: 'improvement',
    shippedAt: '2026-05-07T09:30:00Z',
    titleKey: 'whatsnew.entry.clientListAlphaBar.title',
    bodyKey: 'whatsnew.entry.clientListAlphaBar.body',
  },
  {
    id: '2026-05-07-weather-tip',
    kind: 'feature',
    shippedAt: '2026-05-07T08:00:00Z',
    titleKey: 'whatsnew.entry.weatherTip.title',
    bodyKey: 'whatsnew.entry.weatherTip.body',
  },
  {
    id: '2026-05-07-transformations',
    kind: 'feature',
    shippedAt: '2026-05-07T06:00:00Z',
    titleKey: 'whatsnew.entry.transformations.title',
    bodyKey: 'whatsnew.entry.transformations.body',
  },
];

/** Returns entries shipped within the lookback window. Caller decides whether
 *  to surface the popup based on whether the result is non-empty. */
export function getRecentChanges(now: Date = new Date()): ChangelogEntry[] {
  const cutoffMs = now.getTime() - WHATS_NEW_WINDOW_HOURS * 60 * 60 * 1000;
  return CHANGELOG.filter((e) => Date.parse(e.shippedAt) >= cutoffMs);
}
